import { useState } from 'react';
import { toast } from 'react-toastify';
import { Bell, Lock, User, ChevronRight, Save, Moon, Sun } from 'lucide-react';
import { Card, Button } from '@components/ui';
import { useDispatch, useSelector } from 'react-redux';
import { setTheme } from '@store/slices/uiSlice';
import clsx from 'clsx';

/**
 * Şantiye Şefi Ayarlar Sayfası
 * Basitleştirilmiş versiyon - Şef yetkileri
 * Ekip yönetimi ve şirket ayarları YOK
 */

const TABS = [
    { id: 'profil', label: 'Profil', description: 'Kişisel bilgiler', icon: User, color: 'primary' },
    { id: 'bildirimler', label: 'Bildirimler', description: 'Bildirim tercihleri', icon: Bell, color: 'tertiary' },
    { id: 'guvenlik', label: 'Güvenlik', description: 'Şifre ve güvenlik', icon: Lock, color: 'error' },
];

export default function SefAyarlar() {
    const dispatch = useDispatch();
    const { theme } = useSelector((state) => state.ui);
    const [activeTab, setActiveTab] = useState('profil');

    const colorClasses = {
        primary: 'bg-primary/10 text-primary',
        tertiary: 'bg-tertiary/10 text-tertiary',
        error: 'bg-error/10 text-error',
    };

    const handleThemeChange = (newTheme) => {
        dispatch(setTheme(newTheme));
        toast.success(`Tema ${newTheme === 'dark' ? 'koyu' : 'açık'} moda değiştirildi`);
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'profil':
                return <ProfilAyarlari theme={theme} onThemeChange={handleThemeChange} />;
            case 'bildirimler':
                return <BildirimAyarlari />;
            case 'guvenlik':
                return <GuvenlikAyarlari />;
            default:
                return null;
        }
    };

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-text-primary">Ayarlar</h1>
                <p className="text-text-tertiary mt-1">Uygulama tercihlerinizi yönetin</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Sidebar */}
                <div className="w-full lg:w-72 shrink-0">
                    <Card padding="none" className="overflow-hidden">
                        <div className="p-4 border-b border-border-light bg-gradient-to-r from-bg-tertiary/50 to-transparent">
                            <h3 className="font-semibold text-text-primary">Ayar Kategorileri</h3>
                        </div>
                        <nav className="p-2">
                            {TABS.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = activeTab === tab.id;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={clsx(
                                            'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl mb-1 transition-all text-left cursor-pointer',
                                            isActive
                                                ? 'bg-primary text-white shadow-lg shadow-primary/20'
                                                : 'text-text-secondary hover:bg-bg-tertiary'
                                        )}
                                    >
                                        <div className={clsx(
                                            'w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors',
                                            isActive ? 'bg-white/20' : colorClasses[tab.color]
                                        )}>
                                            <Icon size={18} className={isActive ? 'text-white' : ''} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={clsx('font-medium', isActive ? 'text-white' : 'text-text-primary')}>
                                                {tab.label}
                                            </p>
                                            <p className={clsx('text-xs truncate', isActive ? 'text-white/70' : 'text-text-muted')}>
                                                {tab.description}
                                            </p>
                                        </div>
                                        <ChevronRight
                                            size={16}
                                            className={clsx(
                                                'shrink-0 transition-opacity',
                                                isActive ? 'text-white opacity-100' : 'text-text-muted opacity-0'
                                            )}
                                        />
                                    </button>
                                );
                            })}
                        </nav>
                    </Card>
                </div>

                {/* Content */}
                <div className="flex-1">
                    <Card padding="lg">
                        {renderContent()}
                    </Card>
                </div>
            </div>
        </div>
    );
}

// Profil Ayarları
function ProfilAyarlari({ theme, onThemeChange }) {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-text-primary mb-1">Görünüm</h3>
                <p className="text-sm text-text-muted">Uygulama görünümünü özelleştirin</p>
            </div>

            <div className="flex items-center justify-between p-4 bg-bg-tertiary rounded-xl">
                <div className="flex items-center gap-3">
                    {theme === 'dark' ? <Moon size={20} className="text-info" /> : <Sun size={20} className="text-warning" />}
                    <div>
                        <p className="font-medium text-text-primary">Tema</p>
                        <p className="text-sm text-text-muted">{theme === 'dark' ? 'Koyu mod aktif' : 'Açık mod aktif'}</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => onThemeChange('light')}
                        className={clsx(
                            "p-3 rounded-lg transition-all",
                            theme === 'light' ? "bg-primary text-white" : "bg-bg-secondary text-text-muted hover:bg-bg-hover"
                        )}
                    >
                        <Sun size={18} />
                    </button>
                    <button
                        onClick={() => onThemeChange('dark')}
                        className={clsx(
                            "p-3 rounded-lg transition-all",
                            theme === 'dark' ? "bg-primary text-white" : "bg-bg-secondary text-text-muted hover:bg-bg-hover"
                        )}
                    >
                        <Moon size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
}

// Bildirim Ayarları
function BildirimAyarlari() {
    const [settings, setSettings] = useState({
        malzemeBildirimi: true,
        personelBildirimi: true,
        gunlukHatirlatma: true,
        havaDurumu: true,
    });

    const handleToggle = (key) => {
        setSettings(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSave = () => {
        toast.success('Bildirim ayarları kaydedildi');
    };

    const options = [
        { key: 'malzemeBildirimi', label: 'Malzeme Bildirimleri', desc: 'Malzeme teslimat ve stok uyarıları' },
        { key: 'personelBildirimi', label: 'Personel Bildirimleri', desc: 'Personel atama ve yoklama bildirimleri' },
        { key: 'gunlukHatirlatma', label: 'Günlük Hatırlatma', desc: 'Şantiye günlüğü hatırlatması' },
        { key: 'havaDurumu', label: 'Hava Durumu', desc: 'Hava durumu uyarıları' },
    ];

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-text-primary mb-1">Bildirim Tercihleri</h3>
                <p className="text-sm text-text-muted">Hangi bildirimleri almak istediğinizi seçin</p>
            </div>

            <div className="space-y-3">
                {options.map(opt => (
                    <div key={opt.key} className="flex items-center justify-between p-4 bg-bg-tertiary rounded-xl">
                        <div>
                            <p className="font-medium text-text-primary">{opt.label}</p>
                            <p className="text-sm text-text-muted">{opt.desc}</p>
                        </div>
                        <button
                            onClick={() => handleToggle(opt.key)}
                            className={clsx(
                                "w-12 h-6 rounded-full transition-colors relative",
                                settings[opt.key] ? "bg-success" : "bg-bg-secondary"
                            )}
                        >
                            <div className={clsx(
                                "w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow",
                                settings[opt.key] ? "translate-x-6" : "translate-x-0.5"
                            )} />
                        </button>
                    </div>
                ))}
            </div>

            <Button variant="success" icon={<Save size={18} />} onClick={handleSave}>
                Kaydet
            </Button>
        </div>
    );
}

// Güvenlik Ayarları
function GuvenlikAyarlari() {
    const [passwords, setPasswords] = useState({
        current: '',
        new: '',
        confirm: '',
    });

    const handleSave = () => {
        if (passwords.new !== passwords.confirm) {
            toast.error('Yeni şifreler eşleşmiyor');
            return;
        }
        if (passwords.new.length < 6) {
            toast.error('Şifre en az 6 karakter olmalı');
            return;
        }
        toast.success('Şifre güncellendi');
        setPasswords({ current: '', new: '', confirm: '' });
    };

    const inputClass = "w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:ring-2 focus:ring-primary";

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-text-primary mb-1">Şifre Değiştir</h3>
                <p className="text-sm text-text-muted">Hesap güvenliğiniz için şifrenizi düzenli olarak değiştirin</p>
            </div>

            <div className="space-y-4 max-w-md">
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Mevcut Şifre</label>
                    <input
                        type="password"
                        className={inputClass}
                        value={passwords.current}
                        onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Yeni Şifre</label>
                    <input
                        type="password"
                        className={inputClass}
                        value={passwords.new}
                        onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Yeni Şifre (Tekrar)</label>
                    <input
                        type="password"
                        className={inputClass}
                        value={passwords.confirm}
                        onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
                    />
                </div>
            </div>

            <Button variant="primary" icon={<Lock size={18} />} onClick={handleSave}>
                Şifre Değiştir
            </Button>
        </div>
    );
}
